import React from 'react';

import { CustomerCreditCTA } from './CustomerCreditCTA';
import type { UsePosPaymentsResult } from '../hooks/usePosPayments';
import type { PaymentWarning } from '../hooks/usePosPayments';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { Calculator } from 'lucide-react';
import { Button } from 'antd';

interface PaymentMethodsPanelProps {
  payments: UsePosPaymentsResult;
  totalDue: number;
  currency?: string;
  showHeader?: boolean;
  readOnly?: boolean;
  sectionRef?: React.Ref<HTMLDivElement>;
  compactRows?: boolean;
}

const buildWarningMessage = (warning: PaymentWarning, currency?: string): string => {
  switch (warning.type) {
    case 'underpayment':
      return `Quedará un saldo deudor de ${getFormattedPrice(warning.amount, currency)}.`;
    case 'overpayment':
      return `Se generará un saldo a favor / vuelto de ${getFormattedPrice(warning.amount, currency)}.`;
    case 'debt':
    default:
      return `La orden se registrará como deuda por ${getFormattedPrice(warning.amount, currency)}.`;
  }
};

export const PaymentMethodsPanel: React.FC<PaymentMethodsPanelProps> = ({
  payments,
  totalDue,
  currency,
  showHeader = true,
  readOnly = false,
  sectionRef,
  compactRows = false,
}) => {
  const {
    paymentMethods,
    splits,
    addSplit,
    removeSplit,
    updateSplitAmount,
    updateSplitMethod,
    fillSplitWithRemaining,
    assignTotalToPrimary,
    computeOutstandingForSplit,
    creditAmount,
    creditDraft,
    setCreditDraft,
    applyCreditAmount,
    useMaxCredit,
    resetCredit,
    availableCredit,
    isDebtOnly,
    setDebtOnly,
    debtNote,
    setDebtNote,
    totals,
    warnings,
    isCreditReadOnly,
  } = payments;

  const showDebtNote = (totals.outstanding > 0 || isDebtOnly) && !readOnly;

  /**
   * RESERVADO
  const remainingLabel = totals.outstanding > 0 ? 'Saldo pendiente' : 'Saldo al día';
  const remainingValue = totals.outstanding > 0 ? totals.outstanding : 0;
  const changeLabel = totals.changeDue > 0 ? 'Vuelto / saldo a favor' : 'Sin saldo a favor';
  */

  return (
    <section
      className={`pos-payments${compactRows ? ' pos-payments--compact' : ''}`}
      aria-label="Pagos y saldos"
      ref={sectionRef}
    >
      {showHeader ? (
        <header className="pos-payments__header">
          <div className="pos-payments__header-copy">
            <h2 className="pos-payments__title">Pagos</h2>
            <p className="pos-payments__subtitle">Total a cobrar: {getFormattedPrice(totalDue, currency)}</p>
          </div>
          {!isDebtOnly ? (
            <button
              type="button"
              className="pos-payments__link"
              onClick={() => assignTotalToPrimary()}
              disabled={readOnly}
            >
              Autoasignar total
            </button>
          ) : null}
        </header>
      ) : null}

      <label className="pos-payments__checkbox">
        <input
          type="checkbox"
          checked={isDebtOnly}
          onChange={(event) => setDebtOnly(event.target.checked)}
          disabled={readOnly}
        />
        Registrar como saldo deudor
      </label>

      {isDebtOnly ? (
        <div className="pos-payments__debt-alert" role="alert">
          Se ocultaron los medios de pago. La venta se registrará completa como deuda.
        </div>
      ) : (
        <fieldset className="pos-payments__group">
          <legend className="pos-payments__group-title">Medios de pago</legend>

          {splits.length === 0 ? (
            <p className="pos-payments__empty">Sin métodos agregados.</p>
          ) : (
            <ul className="pos-payments__list">
              {splits.map((split, index) => {
                const outstandingForSplit = computeOutstandingForSplit(split.id);
                const canFillRemaining = outstandingForSplit > 0.009;

                return (
                  <li key={split.id} className="pos-payments__row">
                    <div className="pos-payments__row-fields">
                      <label className="pos-payments__field">
                        Medio
                        <select
                          value={split.methodId ?? 'none'}
                          onChange={(event) =>
                            updateSplitMethod(
                              split.id,
                              event.target.value === 'none' ? null : Number(event.target.value),
                            )
                          }
                          disabled={readOnly}
                        >
                          <option value="none">Seleccionar…</option>
                          {paymentMethods.map((method) => (
                            <option key={method.id} value={method.id}>
                              {method.name}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="pos-payments__field">
                        Monto
                        <input
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step="0.01"
                          value={split.amount.toString()}
                          onChange={(event) => updateSplitAmount(split.id, Number(event.target.value || '0'))}
                          disabled={readOnly}
                        />
                      </label>
                    </div>

                    <div className="pos-payments__row-actions">
                      <Button
                        type={'default'}
                        className="pos-payments__link"
                        onClick={() => fillSplitWithRemaining(split.id)}
                        disabled={readOnly || !canFillRemaining}
                        icon={<Calculator/>}
                        aria-label={
                          canFillRemaining
                            ? `Calcular restante (${getFormattedPrice(outstandingForSplit, currency)})`
                            : 'Calcular restante'
                        }
                      >
                        {/*Calcular restante*/}
                        {/*{canFillRemaining ? (*/}
                        {/*  <span className="pos-payments__hint">*/}
                        {/*    {getFormattedPrice(outstandingForSplit, currency)}*/}
                        {/*  </span>*/}
                        {/*) : null}*/}
                      </Button>

                      {splits.length > 1 ? (
                        <button
                          type="button"
                          className="pos-payments__remove"
                          onClick={() => removeSplit(split.id)}
                          disabled={readOnly}
                          aria-label={`Eliminar medio ${index + 1}`}
                        >
                          ×
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            className="pos-payments__cta"
            onClick={() => addSplit()}
            disabled={readOnly}
          >
            Agregar método de pago
          </button>
        </fieldset>
      )}

      {!isCreditReadOnly && availableCredit > 0 ? (
        <CustomerCreditCTA
          availableCredit={availableCredit}
          appliedCredit={creditAmount}
          suggestedAmount={totals.outstandingBeforeCredit}
          currency={currency}
          onApply={(amount) => applyCreditAmount(amount)}
          onClear={() => resetCredit()}
          disabled={readOnly || isDebtOnly}
        />
      ) : null}

      {!isDebtOnly && !isCreditReadOnly ? (
        <label className="pos-payments__credit-input">
          Usar saldo manualmente
          <div className="pos-payments__credit-input-row">
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={creditDraft}
              onChange={(event) => setCreditDraft(event.target.value)}
              disabled={readOnly || availableCredit <= 0}
              aria-label="Monto de saldo a aplicar"
            />
            <button
              type="button"
              className="pos-payments__link"
              onClick={() => useMaxCredit()}
              disabled={readOnly || availableCredit <= 0}
            >
              Usar restante
            </button>
            <button
              type="button"
              className="pos-payments__link"
              onClick={() => resetCredit()}
              disabled={readOnly || creditAmount === 0}
            >
              Limpiar saldo
            </button>
          </div>
        </label>
      ) : null}

      {(warnings.length > 0 || totals.outstanding > 0 || totals.changeDue > 0) && !isDebtOnly ? (
        <ul className="pos-payments__warnings" role="status" aria-live="assertive">
          {warnings.map((warning) => (
            <li key={`${warning.type}-${warning.amount}`}>{buildWarningMessage(warning, currency)}</li>
          ))}
        </ul>
      ) : null}

      {showDebtNote ? (
        <label className="pos-payments__note">
          Nota de deuda (opcional)
          <textarea
            rows={2}
            value={debtNote}
            onChange={(event) => setDebtNote(event.target.value)}
          />
        </label>
      ) : null}
    </section>
  );
};

export default PaymentMethodsPanel;
